import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { CORRIDORS, formatAmount, formatDate } from "@/lib/constants";
import { getLocalTxCodes, removeLocalTx, clearLocalTx } from "@/lib/localHistory";
import { Trash2, RefreshCw, Inbox, Repeat } from "lucide-react";
import CopyRefButton from "@/components/CopyRefButton";

export default function MyRecentTransactions() {
  const [codes, setCodes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    const localCodes = getLocalTxCodes();
    setCodes(localCodes);
    const results = await Promise.allSettled(
      localCodes.map((code) => api.get(`/transactions/track/${code}`).then((r) => r.data))
    );
    const out = [];
    results.forEach((r, idx) => {
      if (r.status === "fulfilled") out.push(r.value);
      else out.push({ _error: true, code: localCodes[idx] });
    });
    setItems(out);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const removeCode = (code) => {
    removeLocalTx(code);
    setItems((prev) => prev.filter((t) => t.code !== code));
    setCodes((prev) => prev.filter((c) => c !== code));
  };

  const clearAll = () => {
    if (!window.confirm("Effacer tout l'historique local ? (Vos transactions ne sont pas supprimées chez Swiftlink-Africa, seul votre historique navigateur l'est.)")) return;
    clearLocalTx();
    setCodes([]);
    setItems([]);
  };

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Historique local</div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Mes envois récents</h1>
              <p className="text-gray-600 mt-1 max-w-2xl">
                Retrouvez vos transactions effectuées depuis ce navigateur, même sans compte. L'historique est enregistré localement sur votre appareil.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={fetchAll} disabled={loading} className="sl-btn-outline !py-2 !px-4 text-sm" data-testid="refresh-btn">
                <RefreshCw size={14} /> Rafraîchir
              </button>
              {codes.length > 0 && (
                <button onClick={clearAll} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1 px-3" data-testid="clear-all-btn">
                  <Trash2 size={14} /> Effacer l'historique
                </button>
              )}
            </div>
          </div>

          <div className="sl-card mt-8 p-0 overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-gray-500">Chargement…</div>
            ) : codes.length === 0 ? (
              <div className="p-12 text-center" data-testid="empty-recent">
                <Inbox size={36} className="mx-auto text-gray-300" />
                <p className="text-gray-500 mt-3">Aucun envoi récent sur ce navigateur.</p>
                <Link to="/envoyer" className="sl-btn-primary mt-5 inline-flex" data-testid="empty-send-cta">
                  Faire un envoi
                </Link>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="recent-tx-table">
                <thead className="bg-[var(--sl-bg-alt)] text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left p-4">Référence</th>
                    <th className="text-left p-4">Corridor</th>
                    <th className="text-left p-4">Bénéficiaire</th>
                    <th className="text-right p-4">Montant</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-left p-4">Date</th>
                    <th className="p-4" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) =>
                    t._error ? (
                      <tr key={t.code} className="border-t border-gray-100">
                        <td className="p-4 font-mono">{t.code}</td>
                        <td className="p-4 text-gray-400" colSpan={5}>Transaction introuvable</td>
                        <td className="p-4 text-right">
                          <button onClick={() => removeCode(t.code)} className="text-xs text-red-600 hover:text-red-700" data-testid={`remove-${t.code}`}>
                            <Trash2 size={14} className="inline" /> Retirer
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={t.code} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="p-4 font-mono">
                          <div className="flex items-center gap-1.5">
                            <Link to={`/suivi?code=${t.code}`} className="text-[var(--sl-primary)] font-semibold" data-testid={`tx-link-${t.code}`}>
                              {t.code}
                            </Link>
                            <CopyRefButton code={t.code} compact />
                          </div>
                        </td>
                        <td className="p-4 text-xs whitespace-nowrap">
                          <span className="font-mono">{CORRIDORS[t.corridor || "tg_ga"]?.short}</span>
                        </td>
                        <td className="p-4">{t.beneficiary_name}</td>
                        <td className="p-4 text-right">{formatAmount(t.amount_xof, t.source_currency || "XOF")}</td>
                        <td className="p-4"><StatusBadge status={t.status} cancellationReason={t.cancellation_reason} /></td>
                        <td className="p-4 text-gray-500">{formatDate(t.created_at)}</td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/envoyer?reuse=${t.code}`}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] hover:bg-[var(--sl-primary)]/15 font-medium transition"
                              data-testid={`resend-${t.code}`}
                              title="Refaire un envoi avec ce bénéficiaire"
                            >
                              <Repeat size={12} /> Re-envoyer
                            </Link>
                            <button onClick={() => removeCode(t.code)} className="text-xs text-gray-400 hover:text-red-600 p-1.5" data-testid={`hide-${t.code}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-4">
            ℹ️ L'historique est stocké uniquement dans votre navigateur. Si vous changez d'appareil, vos transactions restent valables : tapez simplement votre code SL-XXXXXXXX sur la page <Link to="/suivi" className="text-[var(--sl-primary)] underline">Suivi</Link>.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
